import { LinearGradient } from 'expo-linear-gradient';
import React from 'react';
import { View } from 'react-native';

/**
 * Cosmetic avatar frames bought from the Coins Store. Each key maps to a
 * client-rendered ring (a solid border, or a gradient ring with an optional
 * glow) - no image assets. Unknown / null keys render the avatar bare.
 */
type FrameStyle = { ring: string; gradient?: readonly [string, string]; glow?: boolean };

const FRAMES: Record<string, FrameStyle> = {
  frame_bronze: { ring: '#CD7F32' },
  frame_silver: { ring: '#C7CDDA' },
  frame_gold: { ring: '#F4C84B' },
  frame_volt: { ring: '#22D3FF', glow: true },
  frame_gradient: { ring: '#22D3FF', gradient: ['#22D3FF', '#D600FF'] },
  frame_champion: { ring: '#F4C84B', gradient: ['#F4C84B', '#FF8A00'], glow: true },
};

export function hasFrame(key: string | null | undefined): boolean {
  return Boolean(key && FRAMES[key]);
}

export function AvatarFrame({
  frameKey,
  size,
  children,
}: {
  frameKey: string | null | undefined;
  /** Diameter of the avatar inside the frame. */
  size: number;
  children: React.ReactNode;
}) {
  const f = frameKey ? FRAMES[frameKey] : null;
  if (!f) return <>{children}</>;

  const ring = Math.max(2, Math.round(size * 0.07));
  const gap = Math.max(2, Math.round(size * 0.04));
  const outer = size + (ring + gap) * 2;
  const glow = f.glow
    ? { shadowColor: f.ring, shadowOpacity: 0.7, shadowRadius: 8, shadowOffset: { width: 0, height: 0 }, elevation: 8 }
    : null;

  if (f.gradient) {
    return (
      <LinearGradient
        colors={f.gradient}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={[
          { width: outer, height: outer, borderRadius: outer / 2, alignItems: 'center', justifyContent: 'center' },
          glow,
        ]}
      >
        <View
          style={{
            width: size + gap * 2,
            height: size + gap * 2,
            borderRadius: (size + gap * 2) / 2,
            backgroundColor: '#0B0B1A',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {children}
        </View>
      </LinearGradient>
    );
  }

  return (
    <View
      style={[
        {
          width: outer,
          height: outer,
          borderRadius: outer / 2,
          borderWidth: ring,
          borderColor: f.ring,
          alignItems: 'center',
          justifyContent: 'center',
        },
        glow,
      ]}
    >
      {children}
    </View>
  );
}
